import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { motion, AnimatePresence } from 'framer-motion';
import { debounce } from 'lodash';
import searchService, { City } from '../../../services/searchService';
import LocationDistanceMap from '../../LocationDistanceMap/LocationDistanceMap';
import { DrivingDistanceResult } from '../../../utils/distanceHelper';
import {
  VerticalType,
  TripType,
  VERTICAL_OPTIONS,
  TRIP_TYPES_BY_VERTICAL,
  DEFAULT_TRIP_TYPE_BY_VERTICAL,
  isSourceFieldVisible,
  isDestinationFieldVisible,
  isReturnLegRequired,
} from '../../../constants/tripTypes';

interface SearchSectionNewProps {
  activeTab: VerticalType;
  setActiveTab: (tab: VerticalType) => void;
  tripType: TripType;
  setTripType: (triptype: TripType) => void;
  source: City | null;
  setSource: (source: City | null) => void;
  destination: City | null;
  setDestination: (destination: City | null) => void;
  departureDate: string;
  setDepartureDate: (date: string) => void;
  returnDate: string;
  setReturnDate: (date: string) => void;
  pickupTime: string;
  setPickupTime: (time: string) => void;
  dropTime: string;
  setDropTime: (time: string) => void;
  sourceSearch: string;
  setSourceSearch: (search: string) => void;
  destSearch: string;
  setDestSearch: (search: string) => void;
  handleSearch: () => void;
  handleClearFilter: () => void;
  isSearchEnabled: boolean;
  showClearButton: boolean;
  animationEnabled?: boolean;
  onDistanceComputed?: (result: DrivingDistanceResult | null) => void;
}

interface SearchSectionNewState {
  sourceCities: City[];
  destinationCities: City[];
  isLoadingSource: boolean;
  isLoadingDest: boolean;
  sourceDropdownOpen: boolean;
  destDropdownOpen: boolean;
}

class SearchSectionNew extends React.Component<SearchSectionNewProps, SearchSectionNewState> {
  sourceRef: React.RefObject<HTMLDivElement>;
  destRef: React.RefObject<HTMLDivElement>;
  departureDateRef: React.RefObject<HTMLInputElement>;
  returnDateRef: React.RefObject<HTMLInputElement>;
  pickupTimeRef: React.RefObject<HTMLInputElement>;
  dropTimeRef: React.RefObject<HTMLInputElement>;

  debouncedSourceSearch: any;
  debouncedDestSearch: any;

  constructor(props: SearchSectionNewProps) {
    super(props);
    
    this.sourceRef = React.createRef();
    this.destRef = React.createRef();
    this.departureDateRef = React.createRef();
    this.returnDateRef = React.createRef();
    this.pickupTimeRef = React.createRef();
    this.dropTimeRef = React.createRef();

    this.state = {
      sourceCities: [],
      destinationCities: [],
      isLoadingSource: false,
      isLoadingDest: false,
      sourceDropdownOpen: false,
      destDropdownOpen: false,
    };

    this.debouncedSourceSearch = debounce(this.performSourceSearch.bind(this), 300);
    this.debouncedDestSearch = debounce(this.performDestSearch.bind(this), 300);
  }

  componentDidUpdate() {
    const {
      activeTab,
      tripType,
      source,
      setSource,
      setSourceSearch,
      destination,
      setDestination,
      setDestSearch,
    } = this.props;

    // Clear a stale field whenever the current vertical/trip-type no longer
    // needs it (currently only Hourly's destination - both fields stay
    // visible for Airport, since each side is just a plain City).
    if (!isSourceFieldVisible(activeTab, tripType) && source !== null) {
      setSource(null);
      setSourceSearch('');
    }
    if (!isDestinationFieldVisible(activeTab, tripType) && destination !== null) {
      setDestination(null);
      setDestSearch('');
    }
  }

  componentDidMount() {
    this.loadInitialCities();
    document.addEventListener('mousedown', this.handleClickOutside);
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside);
  }

  loadInitialCities = async () => {
    try {
      const cities = await searchService.searchCities("", 10);
      this.setState({
        sourceCities: cities,
        destinationCities: cities,
      });
    } catch (error) {
      console.error("Failed to load initial cities", error);
    }
  };

  handleClickOutside = (event: MouseEvent) => {
    if (this.sourceRef.current && !this.sourceRef.current.contains(event.target as Node)) {
      this.setState({ sourceDropdownOpen: false });
    }
    if (this.destRef.current && !this.destRef.current.contains(event.target as Node)) {
      this.setState({ destDropdownOpen: false });
    }
  };

  performSourceSearch = async (query: string) => {
    try {
      this.setState({ isLoadingSource: true });
      const results = await searchService.searchCities(query);
      this.setState({ sourceCities: results });
    } catch (error) {
      console.error("Source search error:", error);
    } finally {
      this.setState({ isLoadingSource: false });
    }
  };

  performDestSearch = async (query: string) => {
    try {
      this.setState({ isLoadingDest: true });
      const results = await searchService.searchCities(query);
      this.setState({ destinationCities: results });
    } catch (error) {
      console.error("Destination search error:", error);
    } finally {
      this.setState({ isLoadingDest: false });
    }
  };

  handleSourceSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    this.props.setSourceSearch(query);
    this.setState({ sourceDropdownOpen: true });
    this.debouncedSourceSearch(query);
  };

  handleDestSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    this.props.setDestSearch(query);
    this.setState({ destDropdownOpen: true });
    this.debouncedDestSearch(query);
  };

  selectSourceCity = (city: City) => {
    this.props.setSource(city);
    this.props.setSourceSearch(city.name);
    this.setState({ sourceDropdownOpen: false });
  };

  selectDestinationCity = (city: City) => {
    this.props.setDestination(city);
    this.props.setDestSearch(city.name);
    this.setState({ destDropdownOpen: false });
  };

  swapLocations = () => {
    const { source, setSource, sourceSearch, setSourceSearch, destination, setDestination, setDestSearch } = this.props;
    
    setSource(destination);
    setSourceSearch(destination ? destination.name : "");
    setDestination(source);
    setDestSearch(source ? source.name : "");
  };

  formatDateForDisplay = (isoDate: string) => {
    if (!isoDate) return "";
    const date = new Date(isoDate);
    return `${date.getDate().toString().padStart(2, "0")}/${(
      date.getMonth() + 1
    )
      .toString()
      .padStart(2, "0")}/${date.getFullYear()}`;
  };

  clearSourceSearch = (e: React.MouseEvent) => {
    e.stopPropagation();
    this.props.setSourceSearch("");
    this.props.setSource(null);
  };

  clearDestSearch = (e: React.MouseEvent) => {
    e.stopPropagation();
    this.props.setDestSearch("");
    this.props.setDestination(null);
  };

  showDepartureDatePicker = () => {
    if (this.departureDateRef.current) {
      this.departureDateRef.current.showPicker();
    }
  };

  showPickupTimePicker = () => {
    if (this.pickupTimeRef.current) {
      this.pickupTimeRef.current.showPicker();
    }
  };

  showReturnDatePicker = () => {
    if (this.returnDateRef.current) {
      this.returnDateRef.current.showPicker();
    }
  };

  showDropTimePicker = () => {
    if (this.dropTimeRef.current) {
      this.dropTimeRef.current.showPicker();
    }
  };

  selectVertical = (vertical: VerticalType) => {
    this.props.setActiveTab(vertical);
    this.props.setTripType(DEFAULT_TRIP_TYPE_BY_VERTICAL[vertical]);
  }

  render() {
    const {
      activeTab,
      tripType,
      setTripType,
      source,
      destination,
      departureDate,
      setDepartureDate,
      returnDate,
      setReturnDate,
      pickupTime,
      setPickupTime,
      dropTime,
      setDropTime,
      sourceSearch,
      destSearch,
      handleSearch,
      handleClearFilter,
      isSearchEnabled,
      showClearButton,
      animationEnabled = true,
      onDistanceComputed,
    } = this.props;

    const {
      sourceCities,
      destinationCities,
      isLoadingSource,
      isLoadingDest,
      sourceDropdownOpen,
      destDropdownOpen,
    } = this.state;

    // Only Hourly hides its destination field - Airport keeps both fields
    // (each is just a plain City pick here already; which side is "the
    // airport" is purely which one gets sent to search, decided on Dashboard).
    const isPickUpVisible = isSourceFieldVisible(activeTab, tripType);
    const isDestinationVisible = isDestinationFieldVisible(activeTab, tripType);
    // Return date/time apply to Round-Trip and Multi-City (both bill a
    // return leg back to source) - One-Way doesn't need one.
    const showReturnDateTime = isReturnLegRequired(tripType);

    const formVariants = {
      hidden: { opacity: 0 },
      visible: { opacity: 1, transition: { duration: 0.2, delay: 0.15 } },
      exit: { opacity: 0, transition: { duration: 0.1 } },
    };

    return (
      <section className="max-w-6xl mx-auto px-4 py-8">
        {/* Trip Vertical tabs */}
        <div className="flex gap-4 items-start justify-center md:justify-start mb-6 rounded-t-lg">
          {VERTICAL_OPTIONS.map((vertical) => (
            <button
              key={vertical.value}
              className={`px-4 py-2 font-medium text-sm rounded-full transition-colors duration-200 flex items-center gap-2 border-4 border-black ${
                activeTab === vertical.value ? "bg-white text-black" : "bg-black text-white"
              }`}
              onClick={() => this.selectVertical(vertical.value)}
            >
              <span>{vertical.label}</span>
            </button>
          ))}
        </div>

        <div className="flex gap-4 items-start justify-center md:justify-start mb-6 rounded-t-lg">
          {TRIP_TYPES_BY_VERTICAL[activeTab].map((option) => (
            <button
              key={option.value}
              className={`px-4 py-2 font-medium text-sm rounded-full transition-colors duration-200 flex items-center gap-2 border-4 border-black ${
                tripType === option.value ? "bg-white text-black" : "bg-black text-white"
              }`}
              onClick={() => setTripType(option.value)}
            >
              <span>{option.label}</span>
            </button>
          ))}
        </div>

        {/* Search form */}
        <div className="relative pb-14">
          <motion.div layout transition={{ duration: 0.3, ease: "easeInOut" }}>
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab}
                variants={formVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
              >
                {/* Pickup and Dropoff Section */}
                <div className="flex items-center justify-between gap-6 flex-col md:flex-row">
                  {/* Pick-up Card - Date/Time always apply */}
                  <div className="flex-1 bg-black text-white rounded-2xl shadow-xl p-6">
                    <div className="flex items-center gap-3 mb-6">
                      <span className="w-3 h-3 rounded-full bg-blue-600" />
                      <h3 className="text-lg font-semibold">Pick - Up</h3>
                    </div>

                    <div className={`grid ${isPickUpVisible ? "grid-cols-3" : "grid-cols-2"} gap-4 text-sm`}>
                      {/* Location */}
                      {isPickUpVisible && (
                      <div className="relative" ref={this.sourceRef}>
                        <div className="text-gray-300 font-medium mb-2">Locations</div>
                        <div
                          className="flex items-center border border-gray-600 rounded-md p-2 bg-gray-900 cursor-pointer relative"
                          onClick={() => this.setState({ sourceDropdownOpen: !sourceDropdownOpen })}
                        >
                          <svg className="w-4 h-4 text-gray-400 mr-2" viewBox="0 0 24 24" fill="none">
                            <path
                              d="M12 13C13.6569 13 15 11.6569 15 10C15 8.34315 13.6569 7 12 7C10.3431 7 9 8.34315 9 10C9 11.6569 10.3431 13 12 13Z"
                              stroke="currentColor"
                              strokeWidth="2"
                            />
                            <path
                              d="M12 22C16 18 20 14.4183 20 10C20 5.58172 16.4183 2 12 2C7.58172 2 4 5.58172 4 10C4 14.4183 8 18 12 22Z"
                              stroke="currentColor"
                              strokeWidth="2"
                            />
                          </svg>
                          <input
                            type="text"
                            className="flex-grow outline-none text-gray-200 placeholder-gray-500 bg-transparent text-xs"
                            placeholder="Select your city"
                            value={sourceSearch}
                            onChange={this.handleSourceSearchChange}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!sourceDropdownOpen) {
                                this.setState({ sourceDropdownOpen: true });
                              }
                            }}
                          />
                          {sourceSearch && (
                            <FontAwesomeIcon
                              icon={faTimes}
                              className="text-gray-400 cursor-pointer hover:text-gray-200 text-xs"
                              onClick={this.clearSourceSearch}
                            />
                          )}
                        </div>
                        {sourceDropdownOpen && (
                          <div className="absolute z-20 w-full mt-1 bg-gray-900 border border-gray-600 rounded-md shadow-lg max-h-48 overflow-auto">
                            {isLoadingSource ? (
                              <div className="p-2 text-center text-gray-500 text-xs">Loading...</div>
                            ) : sourceCities.length > 0 ? (
                              sourceCities.map((city) => (
                                <div
                                  key={city.id}
                                  className="p-2 hover:bg-gray-800 cursor-pointer text-gray-200 text-xs border-b border-gray-700"
                                  onClick={() => this.selectSourceCity(city)}
                                >
                                  <div className="font-medium">{city.name}</div>
                                  <div className="text-gray-400 text-xs">{city.state}, {city.region}</div>
                                </div>
                              ))
                            ) : sourceSearch.length >= 2 ? (
                              <div className="p-2 text-gray-500 text-xs">No cities found</div>
                            ) : (
                              <div className="p-2 text-gray-500 text-xs">Type at least 2 characters</div>
                            )}
                          </div>
                        )}
                      </div>
                      )}

                      {/* Date */}
                      <div>
                        <div className="text-gray-300 font-medium mb-2">Date</div>
                        <div
                          className="flex items-center border border-gray-600 rounded-md p-2 bg-gray-900 cursor-pointer relative"
                          onClick={this.showDepartureDatePicker}
                        >
                          <svg className="w-4 h-4 text-gray-400 mr-2" viewBox="0 0 24 24" fill="none">
                            <path
                              d="M8 7V3M16 7V3M7 11H17M5 21H19C20.1046 21 21 20.1046 21 19V7C21 5.89543 20.1046 5 19 5H5C3.89543 5 3 5.89543 3 7V19C3 20.1046 3.89543 21 5 21Z"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                          <input
                            type="text"
                            className="flex-grow outline-none text-gray-200 placeholder-gray-500 bg-transparent text-xs"
                            placeholder="dd/mm/yyyy"
                            value={departureDate ? this.formatDateForDisplay(departureDate) : ""}
                            readOnly
                          />
                          <input
                            ref={this.departureDateRef}
                            type="date"
                            className="absolute inset-0 opacity-0 cursor-pointer"
                            value={departureDate}
                            onChange={(e) => setDepartureDate(e.target.value)}
                            min={new Date().toISOString().split("T")[0]}
                          />
                        </div>
                      </div>

                      {/* Time */}
                      <div>
                        <div className="text-gray-300 font-medium mb-2">Time</div>
                        <div
                          className="flex items-center border border-gray-600 rounded-md p-2 bg-gray-900 cursor-pointer relative"
                          onClick={this.showPickupTimePicker}
                        >
                          <svg className="w-4 h-4 text-gray-400 mr-2" viewBox="0 0 24 24" fill="none">
                            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                            <path d="M12 6V12L16 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                          </svg>
                          <input
                            type="text"
                            className="flex-grow outline-none text-gray-200 placeholder-gray-500 bg-transparent text-xs"
                            placeholder="--:-- --"
                            value={
                              pickupTime
                                ? new Date(`2000-01-01T${pickupTime}`).toLocaleTimeString([], {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })
                                : ""
                            }
                            readOnly
                          />
                          <input
                            ref={this.pickupTimeRef}
                            type="time"
                            className="absolute inset-0 opacity-0 cursor-pointer"
                            value={pickupTime}
                            onChange={(e) => setPickupTime(e.target.value)}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Swap Icon */}
                  {(isPickUpVisible && isDestinationVisible) && (
                  <div className="flex flex-col items-center gap-4">
                    <button
                      onClick={this.swapLocations}
                      className="bg-blue-600 text-white rounded-full w-10 h-10 flex items-center justify-center hover:bg-blue-700 transition-colors"
                    >
                      <span className="text-lg">↕</span>
                    </button>
                  </div>
                  )}

                  {/* Drop-off Card */}
                  {isDestinationVisible && (
                  <div className="flex-1 bg-black text-white rounded-2xl shadow-xl p-6">
                    <div className="flex items-center gap-3 mb-6">
                      <span className="w-3 h-3 rounded-full bg-blue-600" />
                      <h3 className="text-lg font-semibold">Drop - Off</h3>
                    </div>

                    <div className={`grid ${showReturnDateTime ? "grid-cols-3" : "grid-cols-1"} gap-4 text-sm`}>
                      {/* Location */}
                      <div className="relative" ref={this.destRef}>
                        <div className="text-gray-300 font-medium mb-2">Locations</div>
                        <div
                          className="flex items-center border border-gray-600 rounded-md p-2 bg-gray-900 cursor-pointer relative"
                          onClick={() => this.setState({ destDropdownOpen: !destDropdownOpen })}
                        >
                          <svg className="w-4 h-4 text-gray-400 mr-2" viewBox="0 0 24 24" fill="none">
                            <path
                              d="M12 13C13.6569 13 15 11.6569 15 10C15 8.34315 13.6569 7 12 7C10.3431 7 9 8.34315 9 10C9 11.6569 10.3431 13 12 13Z"
                              stroke="currentColor"
                              strokeWidth="2"
                            />
                            <path
                              d="M12 22C16 18 20 14.4183 20 10C20 5.58172 16.4183 2 12 2C7.58172 2 4 5.58172 4 10C4 14.4183 8 18 12 22Z"
                              stroke="currentColor"
                              strokeWidth="2"
                            />
                          </svg>
                          <input
                            type="text"
                            className="flex-grow outline-none text-gray-200 placeholder-gray-500 bg-transparent text-xs"
                            placeholder="Select your city"
                            value={destSearch}
                            onChange={this.handleDestSearchChange}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!destDropdownOpen) {
                                this.setState({ destDropdownOpen: true });
                              }
                            }}
                          />
                          {destSearch && (
                            <FontAwesomeIcon
                              icon={faTimes}
                              className="text-gray-400 cursor-pointer hover:text-gray-200 text-xs"
                              onClick={this.clearDestSearch}
                            />
                          )}
                        </div>
                        {destDropdownOpen && (
                          <div className="absolute z-10 w-full mt-1 bg-gray-900 border border-gray-600 rounded-md shadow-lg max-h-48 overflow-auto">
                            {isLoadingDest ? (
                              <div className="p-2 text-center text-gray-500 text-xs">Loading...</div>
                            ) : destinationCities.length > 0 ? (
                              destinationCities.map((city) => (
                                <div
                                  key={city.id}
                                  className="p-2 hover:bg-gray-800 cursor-pointer text-gray-200 text-xs border-b border-gray-700"
                                  onClick={() => this.selectDestinationCity(city)}
                                >
                                  <div className="font-medium">{city.name}</div>
                                  <div className="text-gray-400 text-xs">{city.state}, {city.region}</div>
                                </div>
                              ))
                            ) : destSearch.length >= 2 ? (
                              <div className="p-2 text-gray-500 text-xs">No cities found</div>
                            ) : (
                              <div className="p-2 text-gray-500 text-xs">Type at least 2 characters</div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Date - round-trip only */}
                      {showReturnDateTime && (
                      <div>
                        <div className="text-gray-300 font-medium mb-2">Date</div>
                        <div
                          className="flex items-center border border-gray-600 rounded-md p-2 bg-gray-900 cursor-pointer relative"
                          onClick={this.showReturnDatePicker}
                        >
                          <svg className="w-4 h-4 text-gray-400 mr-2" viewBox="0 0 24 24" fill="none">
                            <path
                              d="M8 7V3M16 7V3M7 11H17M5 21H19C20.1046 21 21 20.1046 21 19V7C21 5.89543 20.1046 5 19 5H5C3.89543 5 3 5.89543 3 7V19C3 20.1046 3.89543 21 5 21Z"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                          <input
                            type="text"
                            className="flex-grow outline-none text-gray-200 placeholder-gray-500 bg-transparent text-xs"
                            placeholder="dd/mm/yyyy"
                            value={returnDate ? this.formatDateForDisplay(returnDate) : ""}
                            readOnly
                          />
                          <input
                            ref={this.returnDateRef}
                            type="date"
                            className="absolute inset-0 opacity-0 cursor-pointer"
                            value={returnDate}
                            onChange={(e) => setReturnDate(e.target.value)}
                            min={
                              departureDate ||
                              new Date().toISOString().split("T")[0]
                            }
                          />
                        </div>
                      </div>
                      )}

                      {/* Time - round-trip only */}
                      {showReturnDateTime && (
                      <div>
                        <div className="text-gray-300 font-medium mb-2">Time</div>
                        <div
                          className="flex items-center border border-gray-600 rounded-md p-2 bg-gray-900 cursor-pointer relative"
                          onClick={this.showDropTimePicker}
                        >
                          <svg className="w-4 h-4 text-gray-400 mr-2" viewBox="0 0 24 24" fill="none">
                            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                            <path d="M12 6V12L16 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                          </svg>
                          <input
                            type="text"
                            className="flex-grow outline-none text-gray-200 placeholder-gray-500 bg-transparent text-xs"
                            placeholder="--:-- --"
                            value={
                              dropTime
                                ? new Date(`2000-01-01T${dropTime}`).toLocaleTimeString([], {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })
                                : ""
                            }
                            readOnly
                          />
                          <input
                            ref={this.dropTimeRef}
                            type="time"
                            className="absolute inset-0 opacity-0 cursor-pointer"
                            value={dropTime}
                            onChange={(e) => setDropTime(e.target.value)}
                          />
                        </div>
                      </div>
                      )}
                    </div>
                  </div>
                  )}
                </div>

                {/* Distance/route preview - once both locations are picked. */}
                {source && destination && (
                  <div className="mt-6">
                    <LocationDistanceMap
                      origin={{
                        lat: source.latitude,
                        lng: source.longitude,
                        name: source.name,
                      }}
                      destination={{
                        lat: destination.latitude,
                        lng: destination.longitude,
                        name: destination.name,
                      }}
                      onDistanceComputed={onDistanceComputed}
                    />
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </motion.div>

          {/* Search and Clear buttons */}
          <div className="absolute left-1/2 bottom-0 transform -translate-x-1/2 translate-y-1/2 flex items-center gap-4">
            <button
              className={`text-white font-medium py-3 px-12 rounded-md shadow-md transition-colors duration-200 whitespace-nowrap ${
                !isSearchEnabled
                  ? "bg-blue-300 cursor-not-allowed"
                  : "bg-blue-600 hover:bg-blue-700"
              }`}
              onClick={handleSearch}
              disabled={!isSearchEnabled}
            >
              Search Cabs
            </button>
            {showClearButton && (
              <button
                className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium py-3 px-12 rounded-md shadow-md transition-colors duration-200 whitespace-nowrap"
                onClick={handleClearFilter}
              >
                Clear Filter
              </button>
            )}
          </div>
        </div>
      </section>
    );
  }
}

export default SearchSectionNew;
